import React, { useEffect, useRef } from 'react';
import { FormProps, isCreation, SGSFormInput, SGSFormFileInput, SGSFormSelect, SGSFormWysiwyg, SGSForm, SGSFormSwitch } from 'components/Form';
import { ArticleForm, ArticleMode, ArticleModeRouteNames, ArticleModeRubriqueTypes } from 'core/models';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import * as Yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@material-ui/core';
import { SGSButton, SGSIcon } from 'components';
import { useQuery } from 'core/hooks';
import { actualiteService, rubriqueService, tagService } from 'core/services';
import { Editor } from 'tinymce';

type Props = FormProps<ArticleForm> & {
    /**
     * Envoi le formulaire avec l'id pour déterminer s'il s'agit
     * d'une création / màj suite à l'upload des images.
     */
    mode: ArticleMode;
    onCreate?(data: ArticleForm, id?: number): Promise<any>;
};

const ActualiteArticleForm: React.FC<Props> = ({ mode, defaultValues = {}, ...props }) => {
    const { t } = useTranslation();
    const isCreate = isCreation(props);
    const history = useHistory();
    const onSubmit: (data: ArticleForm, id?: number) => any = isCreate ? props.onCreate : props.onEdit;
    const validationSchema: Yup.SchemaOf<ArticleForm> = Yup.object().shape({
        title: Yup.string().required().max(255),
        incipit: Yup.string().nullable().max(1000),
        thumbnailImage: Yup.mixed().optional(),
        thumbnailImagePath: Yup.string().nullable().optional(),
        deleteThumbnailImage: Yup.bool().optional(),
        rubriqueId: Yup.mixed().required(),
        content: Yup.string().required(),
        sortOrder: mode === 'articles' ? Yup.number() : Yup.number().required(),
        breve: Yup.bool().default(false),
        tags: Yup.array().of(Yup.number()),
        id: Yup.number(),
    });

    const methods = useForm<ArticleForm>({
        resolver: yupResolver(validationSchema),
        defaultValues,
    });

    const { handleSubmit, watch, setValue } = methods;
    const { data: rubriques } = useQuery(rubriqueService.getRubriques, ArticleModeRubriqueTypes[mode]);
    const { data: tags } = useQuery(tagService.getTags);

    const editor = useRef<Editor>();
    const curArticleId = useRef<number>(defaultValues.id);
    const watchThumb = watch('thumbnailImage');

    const onDeleteThumbnail = () => {
        setValue('deleteThumbnailImage', true);
    };

    // Lorsque l'on veut uploader une image, cela veut dire qu'on supprime l'ancienne
    useEffect(() => {
        if (!isCreate && watchThumb) {
            setValue('deleteThumbnailImage', true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchThumb]);

    return (
        <FormProvider {...methods}>
            <SGSForm.Card
                onSubmit={handleSubmit(async (data) => {
                    // Création, il nous faut récupérer l'id de l'article, donc on POST en 2 fois
                    if (isCreate) {
                        const { article_id } = await onSubmit({
                            // @ts-ignore
                            ...data,
                            content: '',
                            thumbnailImage: null,
                        });

                        curArticleId.current = article_id;
                    }

                    await editor.current.uploadImages(() =>
                        onSubmit(
                            {
                                // @ts-ignore
                                ...data,
                                content: editor.current.getContent(), // Il faut récupérer le contenu car les URLs des images ont été remplacées à ce moment là
                            },
                            curArticleId.current,
                        ),
                    );
                })}
            >
                <SGSForm.Row>
                    <SGSForm.Col>
                        <SGSFormInput variant="outlined" name="title" label="article.title" />
                        <SGSFormInput multiline variant="outlined" name="incipit" label="article.incipit" />
                    </SGSForm.Col>
                </SGSForm.Row>
                <SGSForm.Row>
                    <SGSForm.Col>
                        <SGSFormFileInput name="thumbnailImage" imagePathFieldName="thumbnailImagePath" onDeleteImage={onDeleteThumbnail} label="article.thumbnail" />
                    </SGSForm.Col>
                </SGSForm.Row>
                <SGSForm.Row>
                    <SGSForm.Col md={6}>
                        <SGSFormSelect
                            name="rubriqueId"
                            variant="outlined"
                            label="article.rubrique"
                            options={
                                rubriques?.map((o) => ({
                                    value: o.id.toString(),
                                    label: o.name,
                                })) ?? []
                            }
                        />
                        <Box display="flex" alignItems="center">
                            <SGSFormSwitch name="breve" label="article.breve" />
                            <SGSIcon title="article.breveInfo" icon="fas fa-question-circle" />
                        </Box>
                    </SGSForm.Col>
                    <SGSForm.Col md={6}>
                        <SGSFormSelect
                            name="tags"
                            variant="outlined"
                            label="help.faq.question.tag"
                            multiple
                            options={
                                tags?.map((o) => ({
                                    value: o.id.toString(),
                                    label: o.name,
                                })) ?? []
                            }
                        />
                        {mode === 'decrypt_rhs' && <SGSFormInput variant="outlined" name="sortOrder" label="article.order" type="number" />}
                    </SGSForm.Col>
                </SGSForm.Row>
                <SGSForm.Row>
                    <SGSForm.Col>
                        <SGSFormWysiwyg
                            name="content"
                            label="article.content"
                            onInit={(e) => (editor.current = e.target)}
                            init={{
                                automatic_uploads: false,
                                images_reuse_filename: false,
                                images_upload_handler(blobInfo, success) {
                                    actualiteService.uploadArticleImage(mode, curArticleId.current, blobInfo).then(success);
                                },
                                height: 300,
                                menubar: false,
                                plugins: ['advlist autolink lists link image', 'charmap print preview anchor help', 'searchreplace visualblocks code', 'insertdatetime media table paste wordcount'],
                                toolbar: 'undo redo | formatselect | image | bold italic | alignleft aligncenter alignright | bullist numlist outdent indent | help',
                            }}
                        />
                    </SGSForm.Col>
                </SGSForm.Row>
                <SGSForm.Actions>
                    <SGSButton
                        variant="outlined"
                        className="mr-2"
                        onClick={() => {
                            history.push(`/${ArticleModeRouteNames[mode]}/articles`);
                        }}
                    >
                        {t('global.back')}
                    </SGSButton>
                    <SGSButton type="submit">{isCreate ? t('global.create') : t('global.save')}</SGSButton>
                </SGSForm.Actions>
            </SGSForm.Card>
        </FormProvider>
    );
};

export default ActualiteArticleForm;
